// ================================================================
// InviteAccept — صفحه‌ی تمام‌صفحه‌ی پذیرش دعوت (/invite/:token)
// ================================================================
// عمومی است (نیاز به لاگین ندارد). کاربر اسم/پسورد می‌ذاره، حساب
// ساخته و فعال می‌شه و مستقیم وارد اپ می‌شه.

import './auth-gate.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { fetchInvite, acceptInvite } from '../../core/auth.js';
import { authShell, bindLangSelect, redrawKeepingInput } from './authShell.js';
import { authField, authError, authSubmit } from './authFields.js';

let rootEl = null;
let token = '';
let invite = null; // { email, organization, role }
let loading = true;
let invalid = false;
let busy = false;
let showPw = false;
let errorMsg = '';

export function showInviteAccept(container, inviteToken) {
  rootEl = container;
  token = inviteToken;
  loading = true;
  invalid = false;
  busy = false;
  showPw = false;
  errorMsg = '';
  invite = null;
  document.title = t('app.title');
  draw();
  load();
}

async function load() {
  try {
    invite = await fetchInvite(token);
  } catch {
    invalid = true;
  } finally {
    loading = false;
    draw();
  }
}

// Siehe authShell.redrawKeepingInput: ohne das leert jedes Neuzeichnen
// (Fehler, Auge, Sprachwechsel) Name und Passwort der Einladung.
function draw() {
  redrawKeepingInput(rootEl, render);
}

function render() {
  if (loading) {
    rootEl.innerHTML = authShell(`
      <h1 class="auth-heading">${escapeHtml(t('invite.title'))}</h1>
      <p class="auth-subtitle" role="status">${escapeHtml(t('invite.loading'))}</p>`, { busy: true });
    bindLangSelect(rootEl, draw);
    return;
  }

  if (invalid) {
    rootEl.innerHTML = authShell(`
      <h1 class="auth-heading">${escapeHtml(t('invite.invalidTitle'))}</h1>
      <p class="auth-subtitle">${escapeHtml(t('invite.invalidText'))}</p>
      <a class="auth-submit" href="/">
        ${icon('arrow_back', 20)}<span>${escapeHtml(t('invite.backToLogin'))}</span>
      </a>`);
    bindLangSelect(rootEl, draw);
    return;
  }

  rootEl.innerHTML = authShell(`
    <h1 class="auth-heading">${escapeHtml(t('invite.title'))}</h1>
    <p class="auth-subtitle">${escapeHtml(t('invite.subtitle', { org: invite.organization }))}</p>

    <form id="inv-form" class="auth-form" novalidate aria-busy="${busy}">
      ${authField({ name: 'email', label: 'invite.email', type: 'email', autocomplete: 'username', value: invite.email, readonly: true })}
      ${invite.role ? `<span class="auth-hint">${escapeHtml(t('invite.roleLine', { role: invite.role }))}</span>` : ''}
      ${authField({ name: 'name', label: 'auth.name', busy, placeholder: 'auth.namePlaceholder' })}
      ${authField({ name: 'password', label: 'invite.password', password: true, showPw, autocomplete: 'new-password', hint: 'auth.passwordHint', required: true, busy, placeholder: 'auth.passwordPlaceholder' })}
      ${authField({ name: 'confirm', label: 'invite.confirm', password: true, showPw, autocomplete: 'new-password', required: true, busy, placeholder: 'auth.passwordPlaceholder' })}
      ${authError(errorMsg)}
      ${authSubmit('invite.cta', busy, 'person_add')}
    </form>`, { busy });

  bindLangSelect(rootEl, draw);
  const form = rootEl.querySelector('#inv-form');
  form.addEventListener('submit', onSubmit);
  // Auge: beide Passwortfelder gemeinsam sichtbar/unsichtbar
  rootEl.querySelector('[data-eye]')?.addEventListener('click', () => { showPw = !showPw; draw(); });
}

async function onSubmit(e) {
  e.preventDefault();
  if (busy) return;

  const form = e.currentTarget;
  const name = (form.elements.namedItem('name')?.value || '').trim();
  const password = form.elements.namedItem('password')?.value || '';
  const confirm = form.elements.namedItem('confirm')?.value || '';

  if (password.length < 8) {
    errorMsg = { key: 'auth.errors.passwordShort' };
    draw();
    return;
  }
  if (password !== confirm) {
    errorMsg = { key: 'invite.errors.mismatch' };
    draw();
    return;
  }

  busy = true;
  errorMsg = '';
  draw();

  try {
    await acceptInvite(token, password, name || undefined);
    window.location.href = '/'; // وارد اپ
  } catch (err) {
    busy = false;
    if (err.status === 409) errorMsg = { key: 'invite.errors.taken' };
    else if (err.status === 400) errorMsg = { key: 'invite.errors.expired' };
    else errorMsg = err.message || { key: 'auth.errors.generic' };
    draw();
  }
}
